<template>
  <div class="admin-page">
    <div class="admin-toolbar">
      <div class="admin-toolbar__actions">
            <el-input
              v-model="searchQuery"
              placeholder="查找用户..."
              class="search-input"
              @input="handleSearch"
            >
              <template #prefix>
                <el-icon><Search /></el-icon>
              </template>
            </el-input>
            <el-button type="primary" @click="navigateToAudit">
              <el-icon><Check /></el-icon>
              用户资料审核
            </el-button>
      </div>
    </div>

    <el-table :data="users" v-loading="loading" style="width: 100%">
          <el-table-column prop="userId" label="ID" width="80" />
          <el-table-column label="头像" width="100">
            <template #default="scope">
              <el-avatar 
                :size="40" 
                :src="scope.row.avatar"
                :alt="scope.row.username"
              >
                {{ scope.row.username?.charAt(0)?.toUpperCase() }}
              </el-avatar>
            </template>
          </el-table-column>
          <el-table-column prop="username" label="用户名" />
          <el-table-column prop="nickname" label="昵称" />
          <el-table-column prop="email" label="邮箱" />
          <el-table-column prop="phone" label="电话" />
          <el-table-column prop="sysRole" label="角色(sys_role)" min-width="160" show-overflow-tooltip />
          <el-table-column prop="loginedAt" label="登录时间">
            <template #default="scope">
              {{ formatDate(scope.row.loginedAt) }}
            </template>
          </el-table-column>
          <el-table-column prop="uuid" label="uuid" />
          <el-table-column prop="createdAt" label="注册时间">
            <template #default="scope">
              {{ formatDate(scope.row.createdAt) }}
            </template>
          </el-table-column>
          <el-table-column label="操作" width="290">
            <template #default="scope">
              <el-button size="small" @click="editUser(scope.row)">编辑</el-button>
              <el-button 
                size="small" 
                type="danger" 
                @click="deleteUser(scope.row)"
              >删除</el-button>
              <el-button
                size="small"
                type="danger"
                plain
                @click="hardDeleteUser(scope.row)"
              >彻底删除</el-button>
            </template>
          </el-table-column>
        </el-table>
        
        <AdminPagination
          v-model:current-page="currentPage"
          v-model:page-size="pageSize"
          :page-sizes="[10, 20, 50, 100]"
          :total="total"
          @size-change="handleSizeChange"
          @current-change="handlePageChange"
        />

    <!-- User Edit Dialog -->
    <el-dialog
      v-model="dialogVisible"
      title="编辑用户"
      width="560px"
      destroy-on-close
    >
      <el-form v-loading="loadingUserRoles" :model="editingUser" label-width="108px">
        <el-form-item label="用户名">
          <el-input v-model="editingUser.username" />
        </el-form-item>
        <el-form-item label="昵称">
          <el-input v-model="editingUser.nickname" />
        </el-form-item>
        <el-form-item label="邮箱">
          <el-input v-model="editingUser.email" />
        </el-form-item>
        <el-form-item label="电话">
          <el-input v-model="editingUser.phone" />
        </el-form-item>
        <el-form-item label="绑定角色">
          <el-select
            v-model="editingRbacRoleIds"
            multiple
            filterable
            placeholder="选择 roles 表中的角色"
            style="width: 100%"
          >
            <el-option
              v-for="r in allRoles"
              :key="r.id"
              :label="r.description ? `${r.name} — ${r.description}` : r.name"
              :value="r.id"
            />
          </el-select>
          <p class="form-hint">保存后写入 user_roles，并同步用户角色字段</p>
        </el-form-item>
      </el-form>
      <template #footer>
        <span class="dialog-footer">
          <el-button @click="dialogVisible = false">取消</el-button>
          <el-button type="primary" :loading="savingUser" @click="saveUser">保存</el-button>
        </span>
      </template>
    </el-dialog>
  </div>
</template>

<script>
import { goToLogin } from '@/utils/loginFlow'
import { ref, onMounted } from 'vue'
import { ElMessage, ElMessageBox } from 'element-plus'
import { Search, Check } from '@element-plus/icons-vue'
import AdminPagination from '@/components/AdminPagination.vue'
import axios from 'axios'
import { useRouter } from 'vue-router'
import {
  listRoles,
  getUserRoles,
  assignUserRole,
  revokeUserRole,
  rbacAuthHeaders
} from '@/utils/rbacApi'

export default {
  name: 'UserManagement',
  components: {
    AdminPagination,
    Search,
    Check
  },
  setup() {
    const loading = ref(false)
    const users = ref([])
    const searchQuery = ref('')
    const currentPage = ref(1)
    const pageSize = ref(10)
    const total = ref(0)
    const dialogVisible = ref(false)
    const editingUser = ref({})
    const editingRbacRoleIds = ref([])
    const initialRbacRoleIds = ref([])
    const allRoles = ref([])
    const loadingUserRoles = ref(false)
    const savingUser = ref(false)

    const router = useRouter()

    const fetchAllRoles = async () => {
      try {
        const res = await listRoles()
        if (res.code === 200) {
          allRoles.value = res.data?.roles || []
        }
      } catch (e) {
        console.error(e)
      }
    }

    // 检查是否登录
    const checkLogin = () => {
      if (!localStorage.getItem('jwt_token')) {
        goToLogin(router)
        return false
      }
      return true
    }

    // Format date
    const formatDate = (timestamp) => {
      if (!timestamp) return ''
      const date = new Date(typeof timestamp === 'number' ? timestamp * 1000 : timestamp)
      return date.toLocaleString()
    }

    // Fetch users
    const fetchUsers = async () => {
      loading.value = true
      try {
        const token = localStorage.getItem('jwt_token')
        const response = await axios.post('/api/v1/admin/user/list', {
          username: searchQuery.value,
          page: currentPage.value,
          pageSize: pageSize.value
        }, {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        })
        
        if (response.data.code === 200) {
          users.value = response.data.data.users
          total.value = response.data.data.total
        }
      } catch (error) {
        console.error('Error fetching users:', error)
        ElMessage.error('获取用户列表失败')
      } finally {
        loading.value = false
      }
    }

    const handleSearch = () => {
      currentPage.value = 1
      fetchUsers()
    }

    const handleSizeChange = (val) => {
      pageSize.value = val
      fetchUsers()
    }

    const handlePageChange = (val) => {
      currentPage.value = val
      fetchUsers()
    }

    const buildApiErrorMessage = (data, fallback) => {
      const message = data?.message?.trim?.() || ''
      const info = data?.info?.trim?.() || ''
      if (message && info && info !== message) {
        return `${message}：${info}`
      }
      return message || info || fallback
    }

    const syncUserRbacRoles = async (user) => {
      const userUuid = user?.uuid?.trim()
      if (!userUuid) {
        throw new Error('该用户缺少 uuid，无法同步 RBAC 角色')
      }
      const next = new Set(editingRbacRoleIds.value.map(Number))
      const prev = new Set(initialRbacRoleIds.value.map(Number))
      for (const roleId of next) {
        if (!prev.has(roleId)) {
          const res = await assignUserRole(userUuid, roleId)
          if (res.code !== 200) {
            throw new Error(res.message || `绑定角色 ${roleId} 失败`)
          }
        }
      }
      for (const roleId of prev) {
        if (!next.has(roleId)) {
          const res = await revokeUserRole(userUuid, roleId)
          if (res.code !== 200) {
            throw new Error(res.message || `移除角色 ${roleId} 失败`)
          }
        }
      }
    }

    const editUser = async (user) => {
      editingUser.value = { ...user }
      editingRbacRoleIds.value = []
      initialRbacRoleIds.value = []
      dialogVisible.value = true
      if (!allRoles.value.length) {
        await fetchAllRoles()
      }
      loadingUserRoles.value = true
      try {
        if (!user.uuid?.trim()) {
          ElMessage.warning('该用户缺少 uuid，无法加载 RBAC 角色')
          return
        }
        const res = await getUserRoles(user.uuid)
        if (res.code === 200) {
          const ids = (res.data?.roles || []).map((r) => r.id)
          editingRbacRoleIds.value = [...ids]
          initialRbacRoleIds.value = [...ids]
        } else {
          ElMessage.warning(res.message || '未能加载用户 RBAC 角色')
        }
      } catch (e) {
        console.error(e)
        ElMessage.warning('加载用户 RBAC 角色失败')
      } finally {
        loadingUserRoles.value = false
      }
    }

    const saveUser = async () => {
      savingUser.value = true
      try {
        const response = await axios.post(
          '/api/v1/admin/user/update',
          editingUser.value,
          { headers: rbacAuthHeaders() }
        )

        if (response.data.code !== 200) {
          ElMessage.error(response.data.message || '更新用户失败')
          return
        }

        await syncUserRbacRoles(editingUser.value)
        const rolesRes = await getUserRoles(editingUser.value.uuid)
        if (rolesRes.code === 200) {
          const names = (rolesRes.data?.roles || []).map((r) => r.name)
          editingUser.value.sysRole = names.length ? names.join(',') : 'user'
        }
        ElMessage.success('更新用户成功，请让用户重新登录以刷新 JWT')
        dialogVisible.value = false
        fetchUsers()
      } catch (error) {
        console.error('Error updating user:', error)
        ElMessage.error(error.message || '更新用户失败')
      } finally {
        savingUser.value = false
      }
    }

    const deleteUser = async (user) => {
      try {
        await ElMessageBox.confirm('确定要删除该用户吗？', '警告', {
          type: 'warning'
        })
        
        const token = localStorage.getItem('jwt_token')
        const response = await axios.post('/api/v1/admin/user/delete', {
          userIds: [user.userId]
        }, {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        })
        
        if (response.data.code === 200) {
          ElMessage.success('删除用户成功')
          fetchUsers()
          return
        }
        ElMessage.error(buildApiErrorMessage(response.data, '删除用户失败'))
      } catch (error) {
        if (error !== 'cancel') {
          console.error('Error deleting user:', error)
          const apiData = error?.response?.data
          ElMessage.error(buildApiErrorMessage(apiData, error?.message || '删除用户失败'))
        }
      }
    }

    const hardDeleteUser = async (user) => {
      const username = user?.username?.trim?.() || ''
      if (!username) {
        ElMessage.error('该用户缺少用户名，无法执行彻底删除')
        return
      }
      try {
        const { value } = await ElMessageBox.prompt(
          `该操作会物理删除用户、余额、个人资料、时刻、文章、课程、消息及主要关联记录，不可恢复。\n请输入用户名「${username}」或 DELETE 确认。`,
          '彻底删除用户',
          {
            type: 'warning',
            inputPlaceholder: `请输入 ${username} 或 DELETE`,
            confirmButtonText: '确认彻底删除',
            cancelButtonText: '取消',
            distinguishCancelAndClose: true
          }
        )

        const response = await axios.post(
          '/api/v1/admin/user/hard-delete',
          {
            userId: user.userId,
            confirmText: value
          },
          { headers: rbacAuthHeaders() }
        )

        if (response.data.code === 200) {
          ElMessage.success(response.data.message || '彻底删除用户成功')
          fetchUsers()
          return
        }
        ElMessage.error(buildApiErrorMessage(response.data, '彻底删除用户失败'))
      } catch (error) {
        if (error !== 'cancel' && error !== 'close') {
          console.error('Error hard deleting user:', error)
          const apiData = error?.response?.data
          ElMessage.error(buildApiErrorMessage(apiData, error?.message || '彻底删除用户失败'))
        }
      }
    }

    const navigateToAudit = () => {
      window.location.href = '/admin/user-audit'
    }

    onMounted(() => {
      // 检查是否登录
      if (!checkLogin()) {
        return
      }
      fetchUsers()
      fetchAllRoles()
    })

    return {
      loading,
      users,
      searchQuery,
      currentPage,
      pageSize,
      total,
      dialogVisible,
      editingUser,
      editingRbacRoleIds,
      allRoles,
      loadingUserRoles,
      savingUser,
      formatDate,
      handleSearch,
      handleSizeChange,
      handlePageChange,
      buildApiErrorMessage,
      editUser,
      saveUser,
      deleteUser,
      hardDeleteUser,
      navigateToAudit
    }
  }
}
</script>

<style scoped>


.dialog-footer {
  display: flex;
  justify-content: flex-end;
  gap: 10px;
}

.form-hint {
  margin: 6px 0 0;
  font-size: 12px;
  color: #909399;
  line-height: 1.4;
}

.search-input {
  width: 280px;
}


</style> 